'use client';

import { useMemo, useRef, useState } from 'react';
import { KAOMOJI_GROUPS } from '@/lib/kaomoji-data';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserKaomojiPicker() {
  const usageReported = useRef(false);
  const [query, setQuery] = useState('');
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>([]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return KAOMOJI_GROUPS;
    return KAOMOJI_GROUPS.map((group) => ({
      ...group,
      items: group.label.toLowerCase().includes(needle)
        ? group.items
        : group.items.filter((item) => item.includes(query.trim())),
    })).filter((group) => group.items.length > 0);
  }, [query]);

  async function copy(item: string) {
    try {
      await navigator.clipboard.writeText(item);
      setCopiedItem(item);
      setRecent((past) => [item, ...past.filter((x) => x !== item)].slice(0, 8));
      setTimeout(() => setCopiedItem((cur) => (cur === item ? null : cur)), 1200);
      if (!usageReported.current) {
        usageReported.current = true;
        void consumeToolUsage('kaomoji', '').catch(() => undefined);
      }
    } catch {
      setCopiedItem(null);
    }
  }

  const button = (item: string) => (
    <button
      key={item}
      onClick={() => void copy(item)}
      style={{
        background: copiedItem === item ? 'var(--accent, #2a78d6)' : 'var(--surface-2, #1c2230)',
        border: '1px solid var(--border, #3a4152)',
        borderRadius: 8,
        color: '#fff',
        cursor: 'pointer',
        fontSize: '1.05rem',
        padding: '0.4rem 0.7rem',
      }}
      title="Copy"
      type="button"
    >
      {copiedItem === item ? 'Copied!' : item}
    </button>
  );

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Pick a kaomoji</h2>
          <p>
            Japanese text faces, one tap to copy. They are plain Unicode, so they paste into any
            chat, bio, or comment.
          </p>
        </div>
        <span className="tool-live-badge">(ツ)</span>
      </div>

      <div className="tool-live-uploader" style={{ gap: '0.9rem' }}>
        <label style={{ width: '100%' }}>
          Filter by mood
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="happy, table, bear…"
            type="text"
            value={query}
          />
        </label>

        {recent.length ? (
          <div style={{ width: '100%' }}>
            <small style={{ opacity: 0.7 }}>Recently copied</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 4 }}>
              {recent.map(button)}
            </div>
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.label} style={{ width: '100%' }}>
            <small style={{ opacity: 0.7 }}>{group.label}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 4 }}>
              {group.items.map(button)}
            </div>
          </div>
        ))}

        {!groups.length ? (
          <p className="tool-live-panel-note">
            Nothing matches that filter; try a mood word like happy or angry.
          </p>
        ) : null}
      </div>
    </article>
  );
}
